// SettingsForm — right-pane Settings form (CHG-1239).
//
// Renders one <fieldset> per field_group inside the given section. Each field
// resolves through @fields/registry; when `pending_primitive: true`, falls
// back to SettingsPendingField (sibling file) covering the 7 logical types
// not yet in the field registry: text, textarea, url, number, select,
// checkbox, radio.
//
// Pure controlled: parent owns `values` + `onChange`. `onSave` is async; the
// parent owns submit-side-effects + dirty tracking semantics.
//
// CHG-1292 (minor-version bump, 4 of 4 in the audit-approved primitive
// extension sequence): SettingsFormProps gained optional
// `savePermission?: { require: string; fallback?: "hide" | "disable" }`.
// The per-form GatedSaveButton subcomponent below ONLY calls
// useHasPermission when a gate is declared — so existing ungated callers
// (and their tests) continue to work without a QueryClientProvider
// context. Default fallback is "disable"; "hide" omits the Save button
// entirely. Form submission is also disabled at the handler level when
// the gate is denied (defense in depth — a user who synthesizes a
// submit event still can't trigger onSave). Field-level
// readonly/disabled is unchanged; this only gates the Save action.

import { useCallback, type FormEvent } from "react";
import { fieldRegistry } from "@fields/registry";
import { SettingsPendingField } from "./SettingsPendingField";
import { useBrand } from "../../contexts/BrandContext";
import { useHasPermission } from "../../auth/usePermissions";
import type { SettingsField, SettingsSection } from "../../lib/settings-sections";
// CHG-1283 — additive "Replay welcome tour" affordance, sibling to the
// CHG-1271 BrandIdentityPreview block. Pure additive insertion; the
// preview component itself is immutable per the v19 brief constraints.
import { ReplayWelcomeButton } from "../welcome/ReplayWelcomeButton";

export interface SettingsFormSavePermission {
  /** Required permission code (e.g. "alora-giving.organization.update"). */
  require: string;
  /** Behavior when the user lacks the permission. Defaults to "disable". */
  fallback?: "hide" | "disable";
}

export interface SettingsFormProps {
  section: SettingsSection;
  values: Record<string, unknown>;
  onChange: (fieldId: string, value: unknown) => void;
  onSave: () => Promise<void> | void;
  busy?: boolean;
  error?: string;
  dirty?: boolean;
  /** CHG-1292 — optional RBAC gate on the Save action. */
  savePermission?: SettingsFormSavePermission;
  /**
   * CHG-2942 — handler for fields declaring an inline `action`
   * (SettingsFieldAction). The button renders only when BOTH the field
   * declares an action AND this handler is passed, so existing callers
   * are unchanged (additive, mirrors the CHG-1292/CHG-1295 precedent).
   */
  onFieldAction?: (fieldId: string, actionId: string) => void;
}

const DENIED_TOOLTIP = "You don't have permission to perform this action";

// ─────────────────────────────────────────────────────────────────
// Plain Save button — no hook calls. Used when savePermission is
// undefined. Keeps ungated callers + their tests working without a
// QueryClientProvider context.
// ─────────────────────────────────────────────────────────────────

function PlainSaveButton({ busy, dirty }: { busy?: boolean; dirty?: boolean }) {
  return (
    <button type="submit" className="btn btn-primary" disabled={busy || !dirty}>
      {busy ? "Saving…" : "Save changes"}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Gated Save button — calls useHasPermission. Only rendered when
// savePermission is declared.
// ─────────────────────────────────────────────────────────────────

function GatedSaveButton({
  gate,
  busy,
  dirty,
}: {
  gate: SettingsFormSavePermission;
  busy?: boolean;
  dirty?: boolean;
}) {
  const allowed = useHasPermission(gate.require);
  if (allowed) {
    return <PlainSaveButton busy={busy} dirty={dirty} />;
  }
  const fallback = gate.fallback ?? "disable";
  if (fallback === "hide") {
    return null;
  }
  return (
    <button
      type="submit"
      className="btn btn-primary"
      title={DENIED_TOOLTIP}
      aria-disabled="true"
      disabled
      data-permission-gate-state="denied"
      data-permission-gate-require={gate.require}
    >
      Save changes
    </button>
  );
}

export function SettingsForm({ section, values, onChange, onSave, busy, error, dirty, savePermission, onFieldAction }: SettingsFormProps) {
  // CHG-1292 — the GatedSaveButton subcomponent below renders a
  // disabled type="submit" button when the user lacks the permission,
  // which blocks the standard submit path. We do NOT call
  // useHasPermission in the SettingsForm body itself — that would
  // force every existing ungated test fixture to mount under a
  // QueryClientProvider. Backend require_permission is the
  // authoritative defense per CHG-1289; this FE layer is UX
  // optimization, not the security boundary.
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (busy) return;
      await onSave();
    },
    [busy, onSave],
  );

  return (
    <form
      onSubmit={handleSubmit}
      data-section-id={section.section_id}
      style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
    >
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 4px" }}>
        <header style={{ marginBottom: 16 }}>
          <h2
            className="font-display"
            style={{ margin: 0, fontSize: 18, fontWeight: 500, color: "var(--color-text)" }}
          >
            {section.label}
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--color-text-muted)" }}>
            {section.description}
          </p>
        </header>

        {error ? (
          <div
            role="alert"
            style={{
              padding: "8px 12px",
              borderRadius: "var(--radius-sm)",
              background: "var(--color-error-tint)",
              color: "var(--color-error)",
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        ) : null}

        {/* CHG-1271 — surface 3: brand-identity preview. Read-only snapshot of
            the active brand variant from useBrand(); shown when section_id is
            brand_identity. Editing UI is a future CHG. */}
        {section.section_id === "brand_identity" ? (
          <>
            <BrandIdentityPreview />
            {/* CHG-1283 — sibling to BrandIdentityPreview; never modifies it. */}
            <ReplayWelcomeButton />
          </>
        ) : null}

        {section.field_groups.map((group) => (
          <fieldset
            key={group.group_id}
            data-group-id={group.group_id}
            style={{
              border: "1px solid var(--color-border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "12px 16px 16px",
              marginBottom: 16,
            }}
          >
            <legend
              style={{
                padding: "0 6px",
                fontSize: 11,
                color: "var(--color-text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 600,
              }}
            >
              {group.label}
            </legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {group.fields.map((field) => (
                <FieldSlot
                  key={field.field_id}
                  field={field}
                  value={values[field.field_id] ?? null}
                  onChange={(v) => onChange(field.field_id, v)}
                  disabled={busy || field.readonly}
                  busy={busy}
                  onAction={
                    field.action && onFieldAction
                      ? () => onFieldAction(field.field_id, field.action!.action_id)
                      : undefined
                  }
                />
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <footer
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 4px 0",
          borderTop: "1px solid var(--color-border-subtle)",
          marginTop: 12,
        }}
      >
        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          {dirty ? "Unsaved changes" : "All changes saved"}
        </span>
        <span style={{ flex: 1 }} />
        {savePermission ? (
          <GatedSaveButton gate={savePermission} busy={busy} dirty={dirty} />
        ) : (
          <PlainSaveButton busy={busy} dirty={dirty} />
        )}
      </footer>
    </form>
  );
}

interface FieldSlotProps {
  field: SettingsField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  /** CHG-2942 — form-busy only (the action button must stay enabled on
   *  readonly fields, so it can't reuse `disabled`). */
  busy?: boolean;
  /** CHG-2942 — pre-bound inline field-action click handler (see SettingsFieldAction). */
  onAction?: () => void;
}

function FieldSlot({ field, value, onChange, disabled, busy, onAction }: FieldSlotProps) {
  const control = field.pending_primitive ? (
    <SettingsPendingField field={field} value={value} onChange={onChange} disabled={disabled} />
  ) : (
    (() => {
      const Field = fieldRegistry[field.field_type];
      if (!Field) {
        return (
          <div role="alert" style={{ fontSize: 12, color: "var(--color-error)" }}>
            Unknown field type: <code>{field.field_type}</code>
          </div>
        );
      }
      return (
        <Field
          name={field.field_id}
          value={value ?? null}
          onChange={onChange}
          label={field.label}
          helperText={field.hint}
          required={field.required}
          disabled={disabled}
        />
      );
    })()
  );

  if (!onAction || !field.action) {
    return control;
  }

  // CHG-2942 — field + inline action button ("Change" beside the read-only
  // contact fields). The wrapper keeps the field full-width (min-w-0 per
  // feedback_ui_composition fieldset rule); marginTop offsets the field's
  // stacked label so the button lines up with the input row.
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>{control}</div>
      <button
        type="button"
        className="btn btn-secondary"
        data-field-action={field.action.action_id}
        onClick={onAction}
        disabled={busy}
        style={{ marginTop: 20, flexShrink: 0 }}
      >
        {field.action.label}
      </button>
    </div>
  );
}

// BrandIdentityPreview — CHG-1271 surface 3.
//
// Renders a read-only snapshot of the active brand variant chrome at the
// top of the Settings Brand Identity section. Pure useBrand consumer; no
// editing UI. Future ε CHG ships the editable variant picker.
function BrandIdentityPreview() {
  const { variantId, chrome } = useBrand();
  return (
    <div
      data-brand-preview="settings-identity"
      data-variant-id={variantId}
      style={{
        border: "1px solid var(--color-border-subtle)",
        borderRadius: "var(--radius-md)",
        padding: "12px 16px",
        marginBottom: 16,
        background: "var(--color-bg-subtle)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        Active brand variant
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span
          className="font-wordmark"
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "var(--color-text)",
            fontFamily: "var(--font-wordmark, 'Poppins', sans-serif)",
          }}
        >
          {chrome.wordmark}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            padding: "2px 8px",
            borderRadius: 9999,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
          }}
        >
          variant: <code>{variantId}</code>
        </span>
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12 }}>
        <Swatch label="Sidebar accent" hex={chrome.sidebar_accent_color} />
        <Swatch label="Login bg" hex={chrome.login_bg_color} />
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-text-muted)" }}>
        Tagline: <em>{chrome.tagline}</em>
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: "var(--color-text-subtle)",
        }}
      >
        Read-only preview. Brand variant resolves from the hostname per
        CHG-1263 §2.2; editing the variant is a future CHG.
      </div>
    </div>
  );
}

function Swatch({ label, hex }: { label: string; hex: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        aria-hidden="true"
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          background: hex,
          border: "1px solid var(--color-border)",
        }}
      />
      <span style={{ color: "var(--color-text-muted)" }}>
        {label}: <code>{hex}</code>
      </span>
    </div>
  );
}
